// Shared field rules for the places a person types their own name or their
// company's name — signup, invite acceptance, and the profile page.
//
// These MIRROR backend/auth/schemas.py, which enforces the same rules at the API
// edge. The client copy exists so the user gets an inline message before a round
// trip; it is not the control. Change one and change the other, or the two
// layers will disagree about the same input.
//
// Letters are matched with \p{L} (any script) rather than A-Za-z: "José",
// "Ştefan" and "李伟" are real names, and rejecting them would be a worse bug
// than the invalid input this guards against. \p{M} admits the combining marks
// that decomposed forms of those names carry.
const NAME_ALLOWED = /^[\p{L}\p{M} '’.·-]+$/u;
const COMPANY_ALLOWED = /^[\p{L}\p{M}\p{N} '’.,&()/+@#-]+$/u;
const HAS_LETTER = /\p{L}/u;

// Collapse runs of whitespace so " John   Smith " and "John Smith" are the same
// value, and a string of spaces can't pass the length check.
const normalize = (value) => (value || "").trim().replace(/\s+/g, " ");

/** Returns an error message, or "" when the name is acceptable. */
export function validateFullName(value) {
  const name = normalize(value);
  if (name.length < 2 || name.length > 100) {
    return "Enter your full name (2–100 characters).";
  }
  if (!HAS_LETTER.test(name) || !NAME_ALLOWED.test(name)) {
    return "Enter a valid name — letters only, no numbers or symbols.";
  }
  return "";
}

/** Returns an error message, or "" when the company name is acceptable.
 *
 * Deliberately looser than a person's name: "3M", "Web 2.0" and "H&M" are real
 * businesses, so digits and trading punctuation are allowed. It still has to
 * contain a letter, which is what rejects "999" and "@#$".
 */
export function validateCompanyName(value) {
  const name = normalize(value);
  if (name.length < 2 || name.length > 255) {
    return "Enter your company name (2–255 characters).";
  }
  if (!HAS_LETTER.test(name) || !COMPANY_ALLOWED.test(name)) {
    return "Enter a valid company name.";
  }
  return "";
}

// Email — one rule for every place an address is typed (invite, signup, sign-in,
// password reset, support form). Mirrors _EMAIL_RE in backend/auth/schemas.py.
//
// The old `\S+@\S+\.\S+` accepted "..@..com", "test@test..com", ".a@b.com",
// "a@b.c" and "a@-b.com" — invalid addresses that both layers waved through, so
// an account really was created against them. This rejects consecutive dots, a
// dot at either end of the local part, a hyphen at the edge of a domain label,
// and a single-character TLD, while still allowing plus-tags, sub-domains and
// punycode (a.b+tag@sub.domain.co.uk, user@xn--p1ai.com).
//
// Deliberately not a full RFC 5322 parser: that accepts quoted strings and
// comments no real signup form wants, and the only complete check is sending
// mail to the address.
// A regex LITERAL, not new RegExp("…"): in a string, \. is not a recognised
// escape and collapses to a bare . (any character), which quietly let
// "a b@c.com" through the very check meant to stop it.
const EMAIL =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;

/** True when `value` is a plausible email address. */
export function isValidEmail(value) {
  const email = (value || "").trim();
  // Bounded like the users.email column (VARCHAR(255)) so an over-long address is
  // refused here rather than dying in the driver as a 500.
  return email.length <= 255 && EMAIL.test(email);
}

/** Returns an error message, or "" when the email is acceptable. */
export function validateEmail(value) {
  const email = (value || "").trim();
  if (!email) return "Enter an email address.";
  if (email.length > 255) return "That email address is too long.";
  if (!EMAIL.test(email)) return "Enter a valid email address.";
  return "";
}

// Phone — mirrors _valid_phone in backend/api/routers/support.py: strip every
// non-digit (space, dash, dot, parens, a leading +) and count what's left.
// E.164 tops out at 15 digits; 7 is a generous floor for the shortest real
// national numbers, so this rejects both "123" and an obviously mistyped
// 17-digit string without rejecting any real international format.
const PHONE_DIGITS = /\D/g;

/** True when `value` has a plausible number of digits for a phone number. */
export function isValidPhone(value) {
  const digits = (value || "").replace(PHONE_DIGITS, "");
  return digits.length >= 7 && digits.length <= 15;
}

/** Returns an error message, or "" when the phone number is acceptable. */
export function validatePhone(value) {
  const phone = (value || "").trim();
  if (!phone) return "Enter a phone number.";
  if (!isValidPhone(phone)) return "Enter a valid phone number.";
  return "";
}
